<template>
  <b-modal
    :id="modalId"
    centered
    title="Add Schedule"
    @hide="$emit('close')"
    hide-header-close
    @show="resetModal"
    @hidden="onHidden"
    @ok.prevent="handleOk"
    size="lg"
  >
    <b-form ref="form" @submit.stop.prevent="handleSubmit">
      <b-row>
        <b-col class="col-4">
          <b-form-group label="Date" :state="getState('date')" invalid-feedback="Date is required.">
            <v-date-picker
              v-model="form.date"
              type="date"
              format="DD-MM-YYYY"
              placeholder="Select Date"
              :disabled-date="disableDateBefore"
              @input="clearError('date')"
            />
          </b-form-group>
        </b-col>

        <b-col class="col-4">
          <b-form-group label="Start Time" :state="getState('start_time')" invalid-feedback="Start Time is required.">
            <v-date-picker
              v-model="form.start_time"
              type="time"
              format="HH:mm"
              placeholder="Select Start Time"
              @input="clearError('start_time')"
            />
          </b-form-group>
        </b-col>

        <b-col class="col-4">
          <b-form-group label="End Time" :state="getState('end_time')" invalid-feedback="End Time is required.">
            <v-date-picker
              v-model="form.end_time"
              type="time"
              format="HH:mm"
              placeholder="Select End Time"
              :disabled="!form.start_time"
              :disabled-time="disableEndTime"
              @input="clearError('end_time')"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-col class="d-flex justify-content-end pr-0">
        <button
          type="button"
          class="btn btn-sm btn-primary ml-auto"
          v-b-tooltip.hover.left="'Add Schedule'"
          @click="addSchedule"
        >
          <span class="fe fe-plus"></span>
          Add
        </button>
      </b-col>

      <b-form-group label="Schedules">
        <vue-good-table
          :columns="columns"
          :rows="form.schedules"
          style-class="vgt-table border-0 table-auto"
          styleClass="vgt-table condensed"
        >
          <div slot="emptystate" class="text-center font-size-14">No data available</div>

          <template slot="table-row" slot-scope="props">
            <span v-if="props.column.field === 'action'" class="d-flex gap-3 justify-content-center align-items-center">
              <button
                class="btn btn-sm btn-warning"
                v-b-tooltip.hover.left="'Delete Schedule'"
                @click="deleteSchedule(props.row)"
              >
                <span class="fe fe-trash"></span>
              </button>
            </span>

            <span v-else-if="props.column.field === 'start_time'">
              <div class="d-flex flex-row align-items-center justify-content-start">
                <span>{{ formatTimeToHHMM(props.row.start_time) }}</span>
              </div>
            </span>

            <span v-else-if="props.column.field === 'end_time'">
              <div class="d-flex flex-row align-items-center justify-content-start">
                <span>{{ formatTimeToHHMM(props.row.end_time) }}</span>
              </div>
            </span>

            <span v-else class="d-flex align-items-center justify-content-start">
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
        </vue-good-table>
      </b-form-group>
    </b-form>

    <template #modal-footer>
      <b-button variant="primary" @click="handleOk" :disabled="form.schedules.length === 0 || isLoading" :key="isLoading">
        <b-spinner v-if="isLoading" small type="grow" class="me-2"></b-spinner>
        <span>{{ isLoading ? "Submitting..." : "Submit" }}</span>
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { formatTimeToHHMM, isPdfFile } from "../utils/utils";
import moment from "moment";

export default {
  name: "ScheduleModal",

  props: {
    modalId: { type: String, default: "schedule-modal" },
    data: { type: Object, default: null },
    isLoading: { type: Boolean, default: false },
  },

  data() {
    return {
      form: this.getInitialForm(),
      formErrors: {},
      suppressValidation: false,
      nextScheduleId: 1,
      columns: [
        {
          label: "Date",
          field: "date",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Start Time",
          field: "start_time",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "End Time",
          field: "end_time",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Action",
          field: "action",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold w-action",
          tdClass: "color-secondary text-center",
        },
      ],
    };
  },

  computed: {},

  methods: {
    formatTimeToHHMM,
    isPdfFile,

    disableDateBefore(date) {
      const today = moment().startOf("day");
      const current = moment(date);
      return current.isBefore(today, "day");
    },

    disableEndTime(time) {
      if (!this.form.start_time) return false;

      const startTime = moment(this.form.start_time);
      const endTime = moment(time);

      return endTime.isSameOrBefore(startTime);
    },

    clearError(field) {
      if (this.formErrors[field]) {
        this.$delete(this.formErrors, field);
      }
    },

    getState(field) {
      if (this.formErrors[field]) return false;
      return null;
    },

    onHidden() {
      this.resetModal();
      this.$emit("close");
    },

    handleOk(bvModalEvt) {
      bvModalEvt.preventDefault();
      this.handleSubmit();
    },

    validateAll() {
      this.formErrors = {};

      if (!this.form.date) this.formErrors.date = "Date is required.";

      if (!this.form.start_time) this.formErrors.start_time = "Start Time is required.";

      if (!this.form.end_time) this.formErrors.end_time = "End Time is required.";

      return Object.keys(this.formErrors).length === 0;
    },

    addSchedule() {
      if (!this.validateAll()) return;

      const formattedDate = this.formatDateOnly(this.form.date);

      const formattedStartTime = this.formatTimeOnly(this.form.start_time);
      const formattedEndTime = this.formatTimeOnly(this.form.end_time);

      this.form.schedules.push({
        id: this.nextScheduleId++,
        date: formattedDate,
        start_time: formattedStartTime,
        end_time: formattedEndTime,
      });

      this.form.date = null;
      this.form.start_time = null;
      this.form.end_time = null;
      this.formErrors = {};
    },

    deleteSchedule(row) {
      const index = this.form.schedules.findIndex((schedule) => schedule.id === row.id);
      if (index > -1) {
        this.form.schedules.splice(index, 1);
      }
    },

    formatDateOnly(dateStr) {
      if (!dateStr) return null;
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    formatTimeOnly(timeStr) {
      if (!timeStr) return null;
      return moment(timeStr).format("HH:mm:ssZ");
    },

    handleSubmit() {
      const schedules = this.form.schedules.map((schedule) => ({
        date: schedule.date,
        start_time: schedule.start_time,
        end_time: schedule.end_time,
      }));

      const payload = {
        id: this.data.id,
        schedules,
      };

      this.$emit("submit-schedule", payload);
    },

    getInitialForm() {
      return {
        date: null,
        start_time: null,
        end_time: null,
        schedules: [],
      };
    },

    async resetModal() {
      if (this.isLoading) return;

      this.suppressValidation = true;

      await this.$nextTick();

      this.form = this.getInitialForm();
      this.formErrors = {};

      this.$nextTick(() => {
        this.suppressValidation = false;
      });
    },
  },

  beforeDestroy() {},
};
</script>

<style scoped>
:deep(.table-header) {
  font-size: 11px !important;
}

:deep(.vgt-table td, .vgt-table th) {
  font-size: 11px !important;
}
</style>
